const TZ = "Australia/Brisbane";
const KEEP = new Set(["QTIS", "F&M", "BM", "TAB", "SP", "WFA", "2YO", "3YO", "VOBIS", "QTIS3YO"]);
const SPLIT = /^(BENCHMARK|RATINGS|RATING|CLASS|MAIDEN|QTIS|OPEN|GROUP|LISTED|RESTRICTED|F&M|FILLIES|COLTS|MARES|TWO-YEAR-OLD|THREE-YEAR-OLD|2YO|3YO|SET|HIGHWAY|PROVINCIAL|COUNTRY)$/;

const isCaps = (w) => /[A-Z]/.test(w) && w === w.toUpperCase();
const titleWord = (w) => (KEEP.has(w) ? w : w.toLowerCase().replace(/(^|[-/(])([a-z])/g, (_, a, b) => a + b.toUpperCase()));
const tidy = (s) => s.replace(/(\d+)\s*-\s*(\d+)/g, "$1–$2").replace(/\s+/g, " ").trim();
const nice = (words) => tidy(words.map((w) => (isCaps(w) ? titleWord(w) : w)).join(" "));

// Splits "LADBROKES LYS GRACIEUX 2019 RATINGS BAND 0 - 65 Handicap" into sponsor + race name.
export const splitRaceName = (name = "") => {
  const words = name.trim().split(/\s+/);
  let caps = 0;
  while (caps < words.length && (isCaps(words[caps]) || !/[a-z]/.test(words[caps]))) caps++;
  if (caps === 0 || caps === words.length) return { sponsor: null, title: nice(words) };
  const k = words.slice(0, caps).findIndex((w, i) => i > 0 && SPLIT.test(w));
  const cut = k > 0 ? k : caps;
  return { sponsor: nice(words.slice(0, cut)), title: nice(words.slice(cut)) };
};

const WEIGHTS = [
  [/set\s*weights?\s*(plus|\+|and)\s*pen/i, "Set weights + penalties"], [/set\s*weights?/i, "Set weights"],
  [/weight\s*for\s*age|\bwfa\b/i, "Weight for age"], [/special\s*weights?/i, "Special weights"],
  [/quality/i, "Quality"], [/handicap|\bhcp\b/i, "Handicap"],
];
const weightCond = (s = "") => WEIGHTS.find(([re]) => re.test(s))?.[1] || null;
const norm = (s = "") => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export const limitsOf = (name = "") => {
  const n = name.toLowerCase();
  const age = /(two|three|four)[-\s]year[-\s]olds?\s*(and|&)\s*(up|upwards|over)|\b([234])\s*-?\s*y\.?o\.?\s*\+|\b([234])\s*yo\s*(and|&)\s*up/.exec(n);
  const ageTxt = age ? `${{ two: 2, three: 3, four: 4 }[age[1]] || age[4] || age[5]}yo+`
    : /two[-\s]year[-\s]old|\b2\s*-?\s*y\.?o\b/.test(n) ? "2yo"
    : /three[-\s]year[-\s]old|\b3\s*-?\s*y\.?o\b/.test(n) ? "3yo"
    : /four[-\s]year[-\s]old|\b4\s*-?\s*y\.?o\b/.test(n) ? "4yo" : null;
  const sex = /fillies\s*(and|&)\s*mares|\bf\s*&\s*m\b/.test(n) ? "Fillies & mares"
    : /colts,?\s*geldings,?\s*(and|&)\s*entires/.test(n) ? "Colts, geldings & entires"
    : /colts,?\s*(and|&)\s*geldings/.test(n) ? "Colts & geldings"
    : /\bfillies\b/.test(n) ? "Fillies" : /\bmares\b/.test(n) ? "Mares"
    : /\bcolts\b/.test(n) ? "Colts" : /\bgeldings\b/.test(n) ? "Geldings" : null;
  return [ageTxt, sex].filter(Boolean);
};

export const ratingsOf = (race) => {
  const src = `${race.race_class || ""} ${race.name || ""}`;
  const m = /(?:ratings?\s*band\s*)?(\d{1,3})\s*-\s*(\d{1,3})/i.exec(src);
  if (m) return { label: "Ratings", value: `${m[1]}–${m[2]}` };
  const cls = race.race_class || "";
  const own = weightCond(cls);
  if (own) return { label: "Weights", value: own };
  if (!cls || norm(race.name).includes(norm(cls))) {
    const w = weightCond(race.name);
    if (w) return { label: "Weights", value: w };
  }
  return cls ? { label: "Class", value: tidy(cls.replace(/benchmark/i, "Benchmark")) } : null;
};

const sydOffset = (ms) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(ms).map((x) => [x.type, x.value]));
  return Date.UTC(+p.year, p.month - 1, +p.day, +p.hour, +p.minute) - ms;
};

export const startMs = (date, hhmm) => {
  if (!date || !/^\d{1,2}:\d{2}$/.test(hhmm || "")) return null;
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  return guess - sydOffset(guess);
};

export const raceMs = (race, date) => (race?.start_utc ? Date.parse(race.start_utc) : startMs(date, race?.start_time));

export const clock12 = (ms) => (ms == null || Number.isNaN(ms) ? ""
  : new Date(ms).toLocaleTimeString("en-AU", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?(am|pm)/i, (x) => ` ${x.trim().toLowerCase()}`));

export const siteDay = (ms) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(ms);

export const to12h = (hhmm) => {
  if (!/^\d{1,2}:\d{2}$/.test(hhmm || "")) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

export const tzLabel = (ms) =>
  new Intl.DateTimeFormat("en-AU", { timeZone: TZ, timeZoneName: "short" }).formatToParts(ms ?? Date.now()).find((p) => p.type === "timeZoneName")?.value || "AET";

export const countdown = (ms, now) => {
  const mins = Math.round((ms - now) / 60000);
  if (mins <= 0) return "Starting now";
  const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
  return `Starts in ${d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`}`;
};

export const sinceText = (iso, now) => {
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  const rel = mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`;
  const sameDay = new Date(iso).toLocaleDateString("en-AU", { timeZone: TZ }) === new Date(now).toLocaleDateString("en-AU", { timeZone: TZ });
  const t = new Date(iso).toLocaleString("en-AU", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true, ...(sameDay ? {} : { weekday: "short" }) }).replace(/\s?(am|pm)/i, (x) => ` ${x.trim().toLowerCase()}`);
  return `${t} (${rel})`;
};

export const goingBand = (c = "") => {
  const k = c.toLowerCase();
  return k.startsWith("firm") ? "firm" : k.startsWith("good") ? "good" : k.startsWith("soft") ? "soft" : k.startsWith("heavy") ? "heavy" : k.startsWith("synthetic") ? "good" : null;
};

export const goingRating = (c = "") => {
  const n = Number(/(\d{1,2})/.exec(c)?.[1]);
  if (n >= 1 && n <= 10) return n;
  return { firm: 2, good: 4, soft: 5, heavy: 8 }[goingBand(c)] || null;
};

export const railText = (rail = "") => {
  const r = rail.trim();
  if (!r || /^true\b/i.test(r)) return "Rail: True";
  return `Rail ${r.replace(/\s*entire(\s+circuit|\s+course)?\s*$/i, ", full course").replace(/\s+,/, ",")}`;
};

export const railOut = (rail = "") => /^\+?\s*(\d+(?:\.\d+)?)\s*m/i.exec(rail.trim())?.[1] || null;

export const goingStick = (info = "") => {
  const num = (re) => re.exec(info)?.[1] || null;
  return {
    index: num(/index[\s:\-=]*([\d.]+)/i) || num(/going\s*stick[\s:\-=]*([\d.]+)/i),
    penetration: num(/penetrat\w*[\s:\-=]*([\d.]+)/i),
    shear: num(/shear[\s:\-=]*([\d.]+)/i),
  };
};
